import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileDown, Loader2, MessageCircle, Plus, Save, Trash2, BadgeEuro } from "lucide-react";
import { api, downloadPdf, errMsg, todayRome, fmtDate } from "@/lib/api";
import { ddtClientWa, ddtVetWa } from "@/lib/ddt";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, Field, NativeSelect, PageHead, Pill } from "@/components/common";
import { SignaturePad } from "@/components/SignaturePad";
import { PayDialog } from "@/components/admin/PayDialog";

const EMPTY = {
  numero: "", data: todayRome(), speditore_nome: "", speditore_indirizzo: "", speditore_ce: "", targa: "",
  dest_pet_heaven: true, dest_ilsap: false, dest_altro: "",
  trasportatore_nome: "FRESILLI CHRISTIAN", trasportatore_indirizzo: "VIA LEONARDO GREPPI 12/A ROMA(RM)-00149", trasportatore_reg: "N° REG ABP7098TRANS-1",
  origine_nome: "", origine_indirizzo: "", luogo_pet_heaven: true, luogo_ilsap: false, luogo_altro: "",
  temperatura: "ambiente", n_colli: "1", contenitore: "RM", righe: [{ specie: "", categoria: "1", quantita: "1", microchip: "" }],
  lavaggio_data: "", lavaggio_ora: "", vet_id: "", pratica_id: null, cliente_nome: "", cliente_telefono: "", note: "",
  trasportatore_piva: "", tipo_servizio: "singola", totale_servizio: 0, pagato: false, pagato_data: "", pagato_metodo: "", firma_speditore: "", asl: false, numero_asl: "", fatturato: true,
};

const PLANTS = [["pet_heaven", "PET HEAVEN SRL", "Via Ugo La Malfa, 19 Spigno Saturnia (LT)"], ["ilsap", "I.L.S.A.P SRL", "Via Capograssa, 996 B.S.Michele (LT)"]];

const fromPratica = (p, vet) => {
  const sped = vet ? { speditore_nome: vet.clinic_name, speditore_indirizzo: `${vet.address || ""} ${vet.city || ""}`.trim(), speditore_ce: vet.n_ce || "" }
    : { speditore_nome: p.nome, speditore_indirizzo: `${p.indirizzo || ""} ${p.citta || ""}`.trim(), speditore_ce: "" };
  return {
    ...sped, origine_nome: sped.speditore_nome, origine_indirizzo: sped.speditore_indirizzo,
    righe: [{ specie: `${p.specie || ""}${p.animale_nome ? ` (${p.animale_nome})` : ""}${p.peso_kg ? ` ${p.peso_kg} kg` : ""}`.trim(), categoria: "1", quantita: "1", microchip: p.microchip || "" }],
    vet_id: p.vet_id || "", pratica_id: p.id, cliente_nome: p.nome, cliente_telefono: p.telefono || "", data: p.data_ritiro || todayRome(),
    tipo_servizio: p.servizio || "singola", totale_servizio: p.totale || 0, pagato: p.stato_pagamento === "saldato", pagato_data: p.saldo_data || "", fatturato: p.fatturato !== false,
  };
};

const PlantChecks = ({ f, setF, prefix }) => (
  <div className="space-y-3">
    {PLANTS.map(([k, name, addr]) => (
      <label key={k} className="flex items-start gap-3 cursor-pointer">
        <Checkbox checked={!!f[`${prefix}_${k}`]} onCheckedChange={(v) => setF((p) => ({ ...p, [`${prefix}_${k}`]: !!v }))} data-testid={`ddt-${prefix}-${k}`} className="mt-1" />
        <span><span className="font-semibold text-[#0D3B52] text-sm">{name}</span><span className="block text-xs text-[#5C6B73]">{addr}</span></span>
      </label>
    ))}
    <Input placeholder="Altro (nome e indirizzo)" value={f[`${prefix}_altro`]} onChange={(e) => setF((p) => ({ ...p, [`${prefix}_altro`]: e.target.value }))} className="bg-white" data-testid={`ddt-${prefix}-altro`} />
  </div>
);

const Block = ({ title, children, className = "" }) => (
  <Card className={`p-6 ${className}`}>
    <p className="text-[11px] uppercase tracking-[0.18em] text-[#997316] font-semibold mb-4">{title}</p>
    <div className="space-y-4">{children}</div>
  </Card>
);

export default function DdtEditor() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [vets, setVets] = useState([]);
  const [settings, setSettings] = useState({ targhe: [], casse: [] });
  const [payOpen, setPayOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    (async () => {
      const [vs, st] = await Promise.all([api.get("/admin/vets"), api.get("/admin/settings")]);
      setVets(vs.data);
      setSettings(st.data);
      const tr = { trasportatore_nome: st.data.trasportatore_nome, trasportatore_indirizzo: st.data.trasportatore_indirizzo, trasportatore_reg: st.data.trasportatore_reg, trasportatore_piva: st.data.trasportatore_piva };
      if (id) {
        const { data } = await api.get(`/admin/ddts/${id}`);
        setF({ ...EMPTY, ...data, vet_id: data.vet_id || "" });
        return;
      }
      const { data: nx } = await api.get("/admin/ddts/next-number");
      const pid = sp.get("pratica");
      let extra = {};
      if (pid) {
        const { data: p } = await api.get(`/admin/pratiche/${pid}`);
        extra = fromPratica(p, vs.data.find((v) => v.id === p.vet_id));
      }
      setF({ ...EMPTY, numero: nx.numero, ...tr, targa: st.data.targhe?.[0] || "", contenitore: st.data.casse?.[0] || "RM", ...extra });
    })().catch((e) => toast.error(errMsg(e)));
  }, [id, sp]);

  const setRow = (i, k, v) => setF((p) => ({ ...p, righe: p.righe.map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));
  const vet = vets.find((v) => v.id === f.vet_id);
  const useVet = () => vet && setF((p) => ({ ...p, speditore_nome: vet.clinic_name, speditore_indirizzo: `${vet.address || ""} ${vet.city || ""}`.trim(), speditore_ce: vet.n_ce || "" }));

  const save = async () => {
    if (!f.data) return toast.error("Inserisci la data");
    setBusy(true);
    const payload = { ...f, vet_id: f.vet_id || null, totale_servizio: Number(f.totale_servizio) || 0 };
    try {
      const { data } = id ? await api.put(`/admin/ddts/${id}`, payload) : await api.post("/admin/ddts", payload);
      toast.success("Documento salvato");
      setF({ ...EMPTY, ...data, vet_id: data.vet_id || "" });
      if (!id) nav(`/admin/ddt/${data.id}`, { replace: true });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const pdf = async () => { try { await downloadPdf(`/ddts/${f.id}/pdf`, `DDT_${f.numero.replace("/", "-")}.pdf`); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div data-testid="admin-ddt-editor">
      <Link to="/admin/ddt" className="inline-flex items-center gap-2 text-sm text-[#5C6B73] hover:text-[#0D3B52] mb-4"><ArrowLeft className="h-4 w-4" />Archivio DDT</Link>
      <PageHead eyebrow={f.asl ? `DDT ASL n. ${f.numero_asl}` : "Documento di trasporto SOA"} title={id ? `DDT n. ${f.numero}` : "Nuovo DDT"} subtitle="Categoria 1 – Reg. CE 1069/2009. Tutti i campi sono modificabili.">
        {f.id && <Pill cls={f.pagato ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"} data-testid="ddt-pay-status">{f.pagato ? `Pagato ${f.pagato_data ? fmtDate(f.pagato_data) : ""}` : "Da pagare"}</Pill>}
        {f.id && <Button variant="outline" onClick={() => setPayOpen(true)} data-testid="ddt-editor-pay" className="rounded-full border-emerald-600 text-emerald-800"><BadgeEuro className="h-4 w-4 mr-1" />{f.pagato ? "Pagamento" : "Segna pagato"}</Button>}
        {f.id && <Button variant="outline" onClick={pdf} data-testid="ddt-editor-pdf" className="rounded-full"><FileDown className="h-4 w-4 mr-1" />PDF</Button>}
        {f.id && f.cliente_telefono && <a href={ddtClientWa(f)} target="_blank" rel="noopener noreferrer" data-testid="ddt-editor-wa-cliente" className="inline-flex items-center gap-1 rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white px-4 h-10 text-sm font-medium"><MessageCircle className="h-4 w-4" />Invia al cliente</a>}
        {f.id && vet?.phone && <a href={ddtVetWa(f, vet.phone)} target="_blank" rel="noopener noreferrer" data-testid="ddt-editor-wa-vet" className="inline-flex items-center gap-1 rounded-full border border-[#25D366] text-[#1DA851] px-4 h-10 text-sm font-medium"><MessageCircle className="h-4 w-4" />Invia al vet</a>}
        <Button onClick={save} disabled={busy} data-testid="ddt-save-button" className="rounded-full bg-[#0D3B52] hover:bg-[#082535]">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />Salva</>}</Button>
      </PageHead>

      <div className="grid lg:grid-cols-2 gap-5">
        <Block title="Documento">
          <div className="grid grid-cols-2 gap-4">
            <Field label="N° DDT" hint={id ? "" : "Assegnato automaticamente al salvataggio se lasci vuoto"}><Input value={f.numero} onChange={set("numero")} className="bg-white" data-testid="ddt-numero" /></Field>
            <Field label="Del"><Input type="date" value={f.data} onChange={set("data")} className="bg-white" data-testid="ddt-data" /></Field>
            <Field label="Targa automezzo">
              <NativeSelect value={f.targa} onChange={set("targa")} data-testid="ddt-targa">
                <option value="">— Seleziona —</option>
                {[...new Set([...(settings.targhe || []), f.targa].filter(Boolean))].map((t) => <option key={t} value={t}>{t}</option>)}
              </NativeSelect>
            </Field>
            <Field label="Visibile al veterinario">
              <NativeSelect value={f.vet_id} onChange={set("vet_id")} data-testid="ddt-vet-select">
                <option value="">— Nessuno —</option>
                {vets.map((v) => <option key={v.id} value={v.id}>{v.clinic_name}</option>)}
              </NativeSelect>
            </Field>
          </div>
        </Block>
        <Block title="Speditore">
          <Field label="Nome"><Input value={f.speditore_nome} onChange={set("speditore_nome")} className="bg-white" data-testid="ddt-speditore-nome" /></Field>
          <Field label="Indirizzo"><Input value={f.speditore_indirizzo} onChange={set("speditore_indirizzo")} className="bg-white" data-testid="ddt-speditore-indirizzo" /></Field>
          <div className="flex gap-3 items-end">
            <Field label="N° CE" className="flex-1"><Input value={f.speditore_ce} onChange={set("speditore_ce")} className="bg-white" data-testid="ddt-speditore-ce" /></Field>
            {vet && <Button type="button" variant="outline" onClick={useVet} data-testid="ddt-use-vet">Usa dati vet</Button>}
          </div>
        </Block>
        <Block title="Destinatario"><PlantChecks f={f} setF={setF} prefix="dest" /></Block>
        <Block title="Trasportatore">
          <Field label="Nome"><Input value={f.trasportatore_nome} onChange={set("trasportatore_nome")} className="bg-white" /></Field>
          <Field label="Indirizzo"><Input value={f.trasportatore_indirizzo} onChange={set("trasportatore_indirizzo")} className="bg-white" /></Field>
          <Field label="N° registrazione"><Input value={f.trasportatore_reg} onChange={set("trasportatore_reg")} className="bg-white" /></Field>
          <Field label="Partita IVA"><Input value={f.trasportatore_piva} onChange={set("trasportatore_piva")} className="bg-white" /></Field>
          <p className="text-xs text-[#5C6B73]">Questi dati compaiono anche come timbro in fondo al documento. Puoi cambiare i valori predefiniti in Impostazioni.</p>
        </Block>
        <Block title="Luogo di origine">
          <Field label="Nome"><Input value={f.origine_nome} onChange={set("origine_nome")} className="bg-white" data-testid="ddt-origine-nome" /></Field>
          <Field label="Indirizzo / N° CE"><Input value={f.origine_indirizzo} onChange={set("origine_indirizzo")} className="bg-white" data-testid="ddt-origine-indirizzo" /></Field>
        </Block>
        <Block title="Luogo di destinazione"><PlantChecks f={f} setF={setF} prefix="luogo" /></Block>
        <Block title="Trasporto">
          <div className="grid grid-cols-3 gap-4">
            <Field label="Temperatura">
              <NativeSelect value={f.temperatura} onChange={set("temperatura")} data-testid="ddt-temperatura">
                <option value="ambiente">Ambiente</option><option value="refrigerato">Refrigerato</option><option value="congelato">Congelato</option>
              </NativeSelect>
            </Field>
            <Field label="N° di colli"><Input value={f.n_colli} onChange={set("n_colli")} className="bg-white" data-testid="ddt-colli" /></Field>
            <Field label="N° contenitore">
              <NativeSelect value={f.contenitore} onChange={set("contenitore")} data-testid="ddt-contenitore">
                {[...new Set([...(settings.casse || []), f.contenitore].filter(Boolean))].map((t) => <option key={t} value={t}>{t}</option>)}
              </NativeSelect>
            </Field>
          </div>
        </Block>
        <Block title="Lavaggio e disinfezione contenitore">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Il"><Input type="date" value={f.lavaggio_data} onChange={set("lavaggio_data")} className="bg-white" /></Field>
            <Field label="Alle ore"><Input type="time" value={f.lavaggio_ora} onChange={set("lavaggio_ora")} className="bg-white" /></Field>
          </div>
        </Block>
        <Block title="Descrizione della merce" className="lg:col-span-2">
          <div className="space-y-2" data-testid="ddt-righe">
            {f.righe.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <Field label={i === 0 ? "Specie animale" : ""} className="col-span-12 sm:col-span-5"><Input value={r.specie} onChange={(e) => setRow(i, "specie", e.target.value)} className="bg-white" data-testid={`ddt-riga-specie-${i}`} /></Field>
                <Field label={i === 0 ? "Categoria" : ""} className="col-span-3 sm:col-span-2"><Input value={r.categoria} onChange={(e) => setRow(i, "categoria", e.target.value)} className="bg-white" /></Field>
                <Field label={i === 0 ? "Quantità" : ""} className="col-span-3 sm:col-span-1"><Input value={r.quantita} onChange={(e) => setRow(i, "quantita", e.target.value)} className="bg-white" /></Field>
                <Field label={i === 0 ? "N° microchip" : ""} className="col-span-5 sm:col-span-3"><Input value={r.microchip} onChange={(e) => setRow(i, "microchip", e.target.value)} className="bg-white" data-testid={`ddt-riga-microchip-${i}`} /></Field>
                <Button type="button" variant="ghost" size="icon" className="col-span-1" disabled={f.righe.length === 1} onClick={() => setF((p) => ({ ...p, righe: p.righe.filter((_, j) => j !== i) }))}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
              </div>
            ))}
          </div>
          {f.righe.length < 8 && (
            <Button type="button" variant="outline" size="sm" onClick={() => setF((p) => ({ ...p, righe: [...p.righe, { specie: "", categoria: "1", quantita: "1", microchip: "" }] }))} data-testid="ddt-add-riga"><Plus className="h-4 w-4 mr-1" />Aggiungi riga</Button>
          )}
        </Block>
        <Block title="Cliente e servizio" className="lg:col-span-2">
          <div className="grid sm:grid-cols-4 gap-4">
            <Field label="Nome cliente"><Input value={f.cliente_nome} onChange={set("cliente_nome")} className="bg-white" data-testid="ddt-cliente-nome" /></Field>
            <Field label="Telefono cliente"><Input value={f.cliente_telefono} onChange={set("cliente_telefono")} className="bg-white" data-testid="ddt-cliente-telefono" /></Field>
            <Field label="Tipo servizio" hint={f.tipo_servizio === "singola" ? "Il PDF include il 2° foglio con le condizioni" : ""}>
              <NativeSelect value={f.tipo_servizio} onChange={set("tipo_servizio")} data-testid="ddt-tipo-servizio">
                <option value="singola">Cremazione singola</option><option value="multipla">Cremazione collettiva</option><option value="smaltimento">Smaltimento</option>
              </NativeSelect>
            </Field>
            <Field label="Totale servizio € (IVA incl.)"><Input type="number" step="0.01" value={f.totale_servizio} onChange={set("totale_servizio")} className="bg-white" data-testid="ddt-totale" /></Field>
            <Field label="Prezzo (uso interno, NON stampato)">
              <NativeSelect value={f.fatturato === false ? "no" : "si"} onChange={(e) => setF((p) => ({ ...p, fatturato: e.target.value === "si" }))} data-testid="ddt-fatturato">
                <option value="si">Ivato / fatturato</option><option value="no">Non ivato / non fatturato</option>
              </NativeSelect>
            </Field>
            <Field label="Note / accessori (stampate sul 2° foglio)" className="sm:col-span-4"><Textarea rows={2} value={f.note} onChange={set("note")} className="bg-white" /></Field>
          </div>
        </Block>
        <Block title="Firma del cliente (speditore)" className="lg:col-span-2">
          <SignaturePad value={f.firma_speditore} onChange={(v) => setF((p) => ({ ...p, firma_speditore: v }))} label="Fai firmare il cliente sullo schermo, poi premi Conferma firma e Salva" />
        </Block>
      </div>
      {f.id && <PayDialog ddt={f} open={payOpen} onOpenChange={setPayOpen} onSaved={(d) => setF((p) => ({ ...p, ...d }))} />}
    </div>
  );
}
